import type { TFunction } from 'i18next';

import { company } from '~/company/Company';

/** Address type the backend keeps when the customer never named the point. */
const DEFAULT_ADDRESS_TYPE = 'home';

/**
 * Address type to save when the customer left the point unnamed: empty for
 * tenants that allow a blank name, the default type everywhere else.
 */
export const defaultAddressType = (): string =>
  company.canDefaultAddressBeEmpty ? '' : DEFAULT_ADDRESS_TYPE;

/** Address fields a point name is derived from. */
export interface AddressNameSource {
  /** Point name chosen by the customer, or the default 'home' type. */
  type?: string | null;
  /** First address line as saved, e.g. 'Laugavegur 12, 101 Reykjavik'. */
  street?: string | null;
  /** City: names the point when the saved address line has no street part. */
  city?: string | null;
}

/** Address fields shown next to a point whose name comes from its street. */
export interface AddressDetailsSource extends AddressNameSource {
  /** Apartment number. */
  apartment?: string | null;
  /** Floor. */
  floor?: string | null;
}

/** Localized labels for the address parts that need one. */
interface AddressDetailsLabels {
  /** Label in front of the apartment number, e.g. 'Apt'. */
  apartment: string;
  /** Label in front of the floor, e.g. 'Floor'. */
  floor: string;
}

/**
 * Whether the point carries the default 'home' type rather than a name the
 * customer typed in.
 */
const isDefaultAddressName = (type?: string | null): boolean => {
  const name = type?.trim();

  return !name || name.toLowerCase() === DEFAULT_ADDRESS_TYPE;
};

/** Street with house number — the first segment of a saved address line. */
const getStreetAndHouse = (street?: string | null): string =>
  street?.split(',')[0]?.trim() ?? '';

/**
 * Point name taken from the address itself: street with house number, falling
 * back to the city when the saved address line carries no street at all (a
 * point picked straight off the map). Empty when neither is known.
 */
const getDerivedAddressName = (source: AddressNameSource): string =>
  getStreetAndHouse(source.street) || source.city?.trim() || '';

/**
 * Whether the point name has to be derived from the address: the tenant's
 * address form has no name field and the address itself is usable.
 */
export const isAddressNameDerived = (source: AddressNameSource): boolean =>
  company.usesDeliveryPickupAddressModal &&
  isDefaultAddressName(source.type) &&
  Boolean(getDerivedAddressName(source));

/**
 * Name to show for a saved point: street and house number for tenants without
 * a location-name field, the tenant's own label everywhere else.
 *
 * @param fallback Name to keep when nothing can be derived — the customer's own
 * name, or the tenant's default label.
 */
export const getAddressName = (
  source: AddressNameSource,
  fallback: string,
): string =>
  isAddressNameDerived(source) ? getDerivedAddressName(source) : fallback;

/** Lowercased and stripped of diacritics: the saved data mixes 'Reykjavík' and 'Reykjavik'. */
const normalizePart = (value: string): string =>
  value
    .trim()
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/**
 * Whether the saved address line already names the city as a part of one of its
 * segments — matched on segment boundaries, so 'Víkurhvarf 3' does not swallow
 * the city 'Vík' while '101 Reykjavik' still matches 'Reykjavík'.
 */
const streetCarriesCity = (street: string, city: string): boolean => {
  const target = normalizePart(city);

  return street.split(',').some((segment) => {
    const part = normalizePart(segment);

    return (
      part === target ||
      part.endsWith(` ${target}`) ||
      part.startsWith(`${target} `)
    );
  });
};

/**
 * Full address under the point name: the whole saved address line, the city when
 * the line does not already carry it, then apartment and floor. The street
 * repeats the derived name on purpose — the complete address stays readable.
 */
const getAddressDetails = (
  source: AddressDetailsSource,
  labels: AddressDetailsLabels,
): string => {
  const street = source.street?.trim();
  const city = source.city?.trim();
  const apartment = source.apartment?.trim();
  const floor = source.floor?.trim();

  return [
    street,
    city && !(street && streetCarriesCity(street, city)) ? city : undefined,
    apartment && `${labels.apartment} ${apartment}`,
    floor && `${labels.floor} ${floor}`,
  ]
    .filter((part): part is string => Boolean(part))
    .join(', ');
};

/**
 * Full address for a derived-name point with the apartment and floor labels
 * taken from the active locale — the single entry point for both address lists,
 * so the modal and the cabinet can never word the line differently.
 */
export const getLocalizedAddressDetails = (
  source: AddressDetailsSource,
  t: TFunction,
): string =>
  getAddressDetails(source, {
    apartment: t('checkout:apt'),
    floor: t('checkout:floor'),
  });
